import { Trans, t } from '@lingui/macro';
import { Search, X, ChevronRight, MessageSquare, Archive, Phone, BellOff, LayoutGrid, Plus, Users, Star } from 'lucide-react';
import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useHistory, useLocation } from '../../hooks/useRouterCompat';
import { useAppUIContext } from '../../context/AppUIContext';
import { Room } from '../../models/Room';
import { RoomCall } from '../../models/RoomCall';
import { Workspace } from '../../models/Workspace';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { cn } from '../../lib/utils';
import { IconChevronDown, IconHash } from './FigmaIcons';
import WorkspaceCreateModal from '../workspaces/WorkspaceCreateModal';
import WorkspaceJoinModal from '../workspaces/WorkspaceJoinModal';

// 왼쪽 메뉴(LeftMenuRooms)와 동일한 채널 정렬:
// 사용자 커스텀 순서(roomOrder/oneToOneOrder, 드래그앤드롭) 우선 → 나머지는 fallback(이름순/날짜순)
function applyRoomOrder(list: Room[], order: string[] | undefined, fallbackSort: (a: Room, b: Room) => number): Room[] {
  if (!order || order.length === 0) return [...list].sort(fallbackSort);
  const ordered = list.filter(r => order.includes(r.id)).sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  const unordered = list.filter(r => !order.includes(r.id)).sort(fallbackSort);
  return [...ordered, ...unordered];
}

interface WorkspaceSitemapModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  autoFocusInput?: boolean;
}

export default function WorkspaceSitemapModal({ open, onOpenChange, autoFocusInput = false }: WorkspaceSitemapModalProps) {
  const { workspaces, rooms, activeCalls, setIsMobileMenuOpen, setActiveWorkspaceId } = useAppUIContext();
  const history = useHistory();
  const location = useLocation();
  // 현재 보고 있는 채널 ID (/channel/:id) — 모달 열 때 이 채널을 선택 상태로
  const currentRoomId = useMemo(() => location.pathname.match(/^\/channel\/(.+)$/)?.[1], [location.pathname]);
  const [filterText, setFilterText] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [_, forceUpdate] = useReducer((x: number) => x + 1, 0 as number);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [showFavoriteOnly, setShowFavoriteOnly] = useState(false);
  // 선택 채널은 ID로 추적 — 워크스페이스/섹션을 접어 목록이 줄어도 다른 채널로 점프하지 않음
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  // 워크스페이스 생성/가입 모달 — 좌측 메뉴 팝오버를 폐기하면서 이 모달로 통합 이전
  const [showCreateWSModal, setShowCreateWSModal] = useState(false);
  const [showJoinWSModal, setShowJoinWSModal] = useState(false);

  // 알림 데이터 (MyNotifs에서 실시간 구독)
  const [notifsByRoom] = useState(() => new Map<string, number>());

  useEffect(() => {
    const unsub = MyNotifs.subscribeToNotificationUpdates(() => {
      notifsByRoom.clear();
      MyNotifs.NotificationsByRoom.forEach((notifs, roomId) => {
        // 노출 대상(음소거·외부 API 비활성화 제외)만 카운트 — 통지센터·좌측 목록과 동일 기준
        const count = notifs.filter(n => n.isNew && n.type !== 'RoomAdded' && n.type !== 'CallStart' && MyNotifs.isVisibleInCenter(n, My.RoomSettings)).length;
        if (count > 0) notifsByRoom.set(roomId, count);
      });
      forceUpdate();
    });
    return unsub;
  }, []);

  // 모달 열릴 때 필터 초기화
  useEffect(() => {
    if (open) {
      setFilterText('');
      setShowUnreadOnly(false);
      setShowFavoriteOnly(false);
    }
  }, [open]);

  // 워크스페이스별 룸 그룹핑
  const groupedData = useMemo(() => {
    const filterLower = filterText.toLowerCase();

    return workspaces.map(ws => {
      const wsRooms = rooms.filter(r => r.workspaceId === ws.id);

      const groupRooms: Room[] = [];
      const oneToOneRooms: Room[] = [];
      const personalRooms: Room[] = [];

      wsRooms.forEach(r => {
        if (r.visibility === 'personal') personalRooms.push(r);
        else if (r.isOneToOne) oneToOneRooms.push(r);
        else groupRooms.push(r);
      });

      // 필터 적용
      const filterRoom = (r: Room) => {
        if (!filterLower) return true;
        const roomName = Room.getRoomName(r, My.user.id).toLowerCase();
        if (roomName.includes(filterLower)) return true;
        // 1:1 룸은 상대방 이름, 이메일로도 검색
        if (r.isOneToOne) {
          const other = Room.getOtherOne2OneUser(r, My.user.id);
          if (other?.name?.toLowerCase().includes(filterLower)) return true;
          if (other?.email?.toLowerCase().includes(filterLower)) return true;
        }
        return false;
      };

      // 미읽음 필터
      const filterUnread = (r: Room) => {
        if (!showUnreadOnly) return true;
        return (notifsByRoom.get(r.id) || 0) > 0;
      };

      // 즐겨찾기 필터 — 즐겨찾기한 채널만 (내 채널 포함 비즐겨찾기는 제외)
      const filterFavorite = (r: Room) => {
        if (!showFavoriteOnly) return true;
        return !!My.RoomSettings.get(r.id)?.favoriteRoomInMyList;
      };

      // 왼쪽 메뉴와 동일한 정렬 적용 (커스텀 순서 우선)
      const filteredGroupRooms = applyRoomOrder(
        groupRooms.filter(filterRoom).filter(filterUnread).filter(filterFavorite),
        My.user.roomOrder,
        (a, b) => a.name.localeCompare(b.name)
      );
      const filteredOneToOne = applyRoomOrder(
        oneToOneRooms.filter(filterRoom).filter(filterUnread).filter(filterFavorite),
        My.user.oneToOneOrder,
        (a, b) => {
          const aDate = a.created instanceof Date ? a.created.getTime() : 0;
          const bDate = b.created instanceof Date ? b.created.getTime() : 0;
          return bDate - aDate;
        }
      );
      const filteredPersonal = personalRooms.filter(filterRoom).filter(filterUnread).filter(filterFavorite);

      // mute(푸시 비활성화) 채널은 워크스페이스 합계 배지에서 제외 — 통지벨·개별 회색 배지와 일치
      const totalUnread = wsRooms.reduce((sum, r) => sum + (My.RoomSettings.get(r.id)?.disablePushNotifications ? 0 : (notifsByRoom.get(r.id) || 0)), 0);
      const totalRooms = filteredGroupRooms.length + filteredOneToOne.length + filteredPersonal.length;

      return {
        workspace: ws,
        groupRooms: filteredGroupRooms,
        oneToOneRooms: filteredOneToOne,
        personalRooms: filteredPersonal,
        totalUnread,
        totalRooms,
      };
    }).filter(g => g.totalRooms > 0 || (!filterText && !showUnreadOnly && !showFavoriteOnly));
  }, [workspaces, rooms, filterText, showUnreadOnly, showFavoriteOnly, _]);

  // 플랫 룸 리스트 빌드 (키보드 네비게이션용)
  const flattenedRooms = useMemo(() => {
    const list: Room[] = [];
    groupedData.forEach(({ workspace, groupRooms, oneToOneRooms, personalRooms }) => {
      const wsKey = workspace.id;
      if (collapsed.has(wsKey)) return; // 워크스페이스가 접혀있으면 하위 룸 전체 생략

      // 왼쪽 메뉴와 동일: '내 채널(personal)'을 Channels 섹션 맨 위에 합쳐서 표시
      if (!collapsed.has(`${wsKey}-rooms`)) list.push(...personalRooms, ...groupRooms);
      if (!collapsed.has(`${wsKey}-dm`)) list.push(...oneToOneRooms);
    });
    return list;
  }, [groupedData, collapsed]);

  // 검색어 변경 시 첫 결과로 선택 이동 (collapse 변경에는 반응 안 함 → 접어도 선택 유지)
  useEffect(() => {
    if (!open || !filterText) return;
    setSelectedRoomId(flattenedRooms[0]?.id ?? null);
    // flattenedRooms 의존성 제외 의도적: filterText 변화에만 첫 결과로 점프
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterText, open]);

  // 모달 열릴 때 1회: 현재 보고 있는 채널을 선택 상태로 (목록이 필터 리셋으로 안정된 뒤)
  const didPositionRef = useRef(false);
  useEffect(() => {
    if (!open) { didPositionRef.current = false; return; }
  }, [open]);
  useEffect(() => {
    if (!open || didPositionRef.current || flattenedRooms.length === 0) return;
    const target = currentRoomId && flattenedRooms.some(r => r.id === currentRoomId)
      ? currentRoomId
      : flattenedRooms[0]?.id ?? null;
    setSelectedRoomId(target);
    didPositionRef.current = true;
  }, [open, flattenedRooms, currentRoomId]);

  // 키보드 네비게이션 (ID 기준 현재 위치를 찾아 이동)
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const idx = flattenedRooms.findIndex(r => r.id === selectedRoomId);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = idx < 0 ? 0 : Math.min(idx + 1, flattenedRooms.length - 1);
        setSelectedRoomId(flattenedRooms[next]?.id ?? null);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = idx < 0 ? 0 : Math.max(idx - 1, 0);
        setSelectedRoomId(flattenedRooms[prev]?.id ?? null);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const room = flattenedRooms.find(r => r.id === selectedRoomId);
        if (room) navigateToRoom(room);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, flattenedRooms, selectedRoomId]);

  // 자동 스크롤 — 선택 채널이 현재 보이는 목록에 있을 때만
  useEffect(() => {
    if (!open || !selectedRoomId) return;
    const el = document.getElementById(`sitemap-room-${selectedRoomId}`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [selectedRoomId, open]);

  const toggleCollapse = (id: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const navigateToRoom = (room: Room) => {
    if (room.workspaceId) setActiveWorkspaceId(room.workspaceId);
    history.replace(`/channel/${room.id}`);
    onOpenChange(false);
    if (window.innerWidth < 1200) setIsMobileMenuOpen(false);
  };

  const navigateToSearch = (roomId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`/search/${roomId}`);
    onOpenChange(false);
    if (window.innerWidth < 1200) setIsMobileMenuOpen(false);
  };

  const navigateToWorkspace = (wsId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveWorkspaceId(wsId);
    history.push(`/ws/${wsId}`);
    onOpenChange(false);
    if (window.innerWidth < 1200) setIsMobileMenuOpen(false);
  };

  function renderRoomItem(room: Room) {
    const isOneToOne = room.isOneToOne;
    const isPersonal = room.visibility === 'personal';
    const roomName = isPersonal ? t`My Channel` : Room.getRoomName(room, My.user.id);
    const otherUser = isOneToOne ? Room.getOtherOne2OneUser(room, My.user.id) : undefined;
    const avatarUrl = isPersonal ? '' : room.profileImageUrl || (isOneToOne ? otherUser?.photoUrl : '') || '';
    const notifCount = notifsByRoom.get(room.id) || 0;
    const isHidden = My.RoomSettings.get(room.id)?.hideRoomInMyList;
    const disabledPush = My.RoomSettings.get(room.id)?.disablePushNotifications;
    const isFavorite = My.RoomSettings.get(room.id)?.favoriteRoomInMyList;
    const call = activeCalls.get(room.id);
    const isCallActive = call && RoomCall.isActive(call);
    const isArchived = Room.isArchivedOneToOne(room);
    const isSelected = room.id === selectedRoomId;

    return (
      <div
        key={room.id}
        id={`sitemap-room-${room.id}`}
        className={cn(
          "relative flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors duration-150 group",
          // 현재 채널: 은은한 틴트 + 좌측 가는 액센트 바 (시선을 과하게 끌지 않게)
          isSelected ? "bg-primary/5 hover:bg-primary/10" : "hover:bg-muted/50 active:bg-muted",
          isHidden && "opacity-50"
        )}
        onClick={() => navigateToRoom(room)}
      >
        {/* 현재 채널 좌측 액센트 바 — 약하지만 위치를 알 수 있게 */}
        {isSelected && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-0.5 rounded-full bg-primary/60" aria-hidden />}
        {/* 아바타 */}
        <Avatar className={cn("w-7 h-7 shrink-0", isOneToOne ? "rounded-full" : "rounded-lg")}>
          {isPersonal ? (
            <div className="w-full h-full bg-primary/15 flex items-center justify-center">
              <Archive className="w-3.5 h-3.5 text-primary" />
            </div>
          ) : (
            <>
              <AvatarImage src={avatarUrl || undefined} className="object-cover" />
              <AvatarFallback className={cn("bg-muted text-muted-foreground text-meta-sm", isOneToOne ? "rounded-full" : "rounded-lg")}>
                {roomName.substring(0, 1)}
              </AvatarFallback>
            </>
          )}
        </Avatar>

        <div className="flex-1 min-w-0 flex items-center gap-1">
          {!isOneToOne && !isPersonal && <span className="text-xs shrink-0 text-muted-foreground">#</span>}
          <span className={cn(
            "truncate text-sm",
            isSelected ? "font-semibold text-[color:rgb(var(--text-body))]" : (notifCount > 0 ? "font-semibold text-[color:rgb(var(--text-body))]" : "text-[color:rgb(var(--text-body))]"),
            isArchived && "text-muted-foreground line-through"
          )}>
            {roomName}
          </span>
          {isFavorite && <span className="text-yellow-500 text-xs shrink-0">★</span>}
        </div>

        {/* 우측 아이콘들 */}
        <div className="flex items-center gap-1 shrink-0">
          {/* 통화중 */}
          {isCallActive && (
            <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
              <Phone className="w-3 h-3 text-green-600" />
            </div>
          )}

          {/* 알림 끔 */}
          {disabledPush && <BellOff className="w-3.5 h-3.5 text-muted-foreground" />}


          {/* 알림 뱃지 */}
          {/* notifCount 는 노출 대상만 카운트 → 음소거 방은 뱃지 안 뜸 */}
          {notifCount > 0 && (
            <Badge variant="notification">
              {notifCount > 99 ? '99+' : notifCount}
            </Badge>
          )}
        </div>
      </div>
    );
  }

  function renderSection(title: string, icon: React.ReactNode, rooms: Room[], sectionId: string) {
    if (rooms.length === 0) return null;
    const isCollapsed = collapsed.has(sectionId);
    // mute(푸시 비활성화) 채널은 섹션 합계 배지에서 제외 — 통지벨·개별 회색 배지와 일치
    const sectionUnread = rooms.reduce((sum, r) => sum + (My.RoomSettings.get(r.id)?.disablePushNotifications ? 0 : (notifsByRoom.get(r.id) || 0)), 0);

    return (
      <div className="mt-1">
        <button
          onClick={() => toggleCollapse(sectionId)}
          className="flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors w-full text-left"
        >
          {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <IconChevronDown className="w-3 h-3" />}
          {icon}
          <span>{title}</span>
          <span className="text-muted-foreground font-normal normal-case">({rooms.length})</span>
          {sectionUnread > 0 && (
            <Badge variant="notification" className="ml-auto scale-90">{sectionUnread}</Badge>
          )}
        </button>
        {!isCollapsed && (
          <div className="ml-2">
            {rooms.map(room => renderRoomItem(room))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideTitle
        className="max-w-lg w-[calc(100%-24px)] h-[80vh] p-0 flex flex-col overflow-hidden rounded-2xl"
        onOpenAutoFocus={(e) => {
          if (!autoFocusInput) e.preventDefault();
        }}
      >
        {/* 헤더 */}
        <div className="shrink-0 px-4 pt-4 pb-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <LayoutGrid className="w-5 h-5 text-primary" />
              <h2 className="text-base font-bold text-foreground"><Trans>All Workspaces</Trans></h2>
            </div>
          </div>

          {/* 필터 입력 */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                autoFocus={autoFocusInput}
                type="text"
                className="w-full h-9 pl-8 pr-8 text-sm rounded-lg border border-border bg-muted/50 focus:bg-background focus:border-primary focus:outline-none transition-colors placeholder:text-muted-foreground"
                placeholder={t`Search channels, users, emails...`}
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              {filterText && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                  onClick={() => setFilterText('')}
                >
                  <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowUnreadOnly(!showUnreadOnly)}
              className={cn(
                "h-9 px-3 rounded-lg text-xs font-medium shrink-0 border transition-colors",
                showUnreadOnly
                  ? "bg-primary/10 border-primary/30 text-primary"
                  : "bg-muted/50 border-border text-muted-foreground hover:bg-muted"
              )}
            >
              <Trans>Unread</Trans>
            </button>
            <button
              onClick={() => setShowFavoriteOnly(!showFavoriteOnly)}
              className={cn(
                "h-9 w-9 flex items-center justify-center rounded-lg shrink-0 border transition-colors",
                showFavoriteOnly
                  ? "bg-primary/10 border-primary/30"
                  : "bg-muted/50 border-border hover:bg-muted"
              )}
              title={t`Show favorites only`}
            >
              <Star className={cn("w-4 h-4 transition-colors", showFavoriteOnly ? "text-yellow-500 fill-yellow-500" : "text-muted-foreground")} />
            </button>
          </div>
        </div>

        {/* 본문 */}
        <div className="flex-1 overflow-y-auto px-2 pb-4 custom-scrollbar">
          {groupedData.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Search className="w-8 h-8 mb-2 opacity-50" />
              <p className="text-sm"><Trans>No matching channels</Trans></p>
            </div>
          ) : (
            groupedData.map(({ workspace: ws, groupRooms, oneToOneRooms, personalRooms, totalUnread }) => {
              const wsKey = ws.id;
              const isWsCollapsed = collapsed.has(wsKey);

              return (
                // 워크스페이스마다 연한 박스로 구분
                <div key={wsKey} className="mb-2.5 rounded-xl border border-border/60 bg-muted/30 px-1.5 pt-1 pb-1.5">
                  {/* 워크스페이스 헤더 */}
                  <div className="flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-muted/60 transition-colors">
                    <button
                      onClick={() => toggleCollapse(wsKey)}
                      className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                    >
                      {isWsCollapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" /> : <IconChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
                      {ws.iconUrl ? (
                        <img src={ws.iconUrl} className="w-7 h-7 rounded-lg object-cover shrink-0 border border-border" alt="" />
                      ) : (
                        <div className="w-7 h-7 rounded-lg bg-[rgba(255,127,0,0.1)] text-primary flex items-center justify-center font-bold text-xs shrink-0 border border-[rgba(255,127,0,0.1)]">
                          {ws.name.charAt(0)}
                        </div>
                      )}
                      <span className="font-semibold text-sm text-foreground truncate">{ws.name}</span>
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {totalUnread > 0 && (
                        <Badge variant="notification">
                          {totalUnread > 99 ? '99+' : totalUnread}
                        </Badge>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveWorkspaceId(ws.id); history.push('/search'); onOpenChange(false); if (window.innerWidth < 1200) setIsMobileMenuOpen(false); }}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                        title={t`Search`}
                      >
                        <Search className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => navigateToWorkspace(ws.id, e)}
                        className="text-meta-sm text-muted-foreground hover:text-primary transition-colors px-1.5 py-0.5 rounded hover:bg-primary/5"
                      >
                        <Trans>Open</Trans>
                      </button>
                    </div>
                  </div>

                  {/* 하위 섹션들 */}
                  {!isWsCollapsed && (
                    <div className="mt-0.5 ml-1.5 pl-1.5 border-l border-border/40">
                      {/* 왼쪽 메뉴와 동일: '내 채널'을 Channels 섹션 맨 위에 합침 (별도 My Channel 섹션 제거) */}
                      {renderSection(t`Channels`, <IconHash className="w-3 h-3" />, [...personalRooms, ...groupRooms], `${wsKey}-rooms`)}
                      {renderSection(t`Direct`, <MessageSquare className="w-3 h-3" />, oneToOneRooms, `${wsKey}-dm`)}
                      {groupRooms.length === 0 && oneToOneRooms.length === 0 && personalRooms.length === 0 && (
                        <p className="px-3 py-2 text-xs text-muted-foreground italic"><Trans>No channels</Trans></p>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* 푸터 — 워크스페이스 생성/가입 (좌측 팝오버에서 이전).
            '워크스페이스' 공통 라벨을 위로 빼고 버튼은 동사만(만들기/참가) → 작은 화면 줄바꿈 방지 */}
        <div className="shrink-0 border-t border-border px-3 py-2.5">
          <p className="text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 px-0.5">
            <Trans>Workspace</Trans>
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { onOpenChange(false); setShowCreateWSModal(true); }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary/5 hover:bg-primary/10 border border-primary/20 text-primary font-semibold text-sm transition-colors cursor-pointer"
            >
              <Plus className="w-4 h-4 shrink-0" />
              <Trans>Create</Trans>
            </button>
            <button
              onClick={() => { onOpenChange(false); setShowJoinWSModal(true); }}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-muted hover:bg-accent border border-border text-[color:rgb(var(--text-body))] font-semibold text-sm transition-colors cursor-pointer"
            >
              <Users className="w-4 h-4 shrink-0" />
              <Trans>Join</Trans>
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Workspace Modals — 사이트맵을 닫고 위로 띄움 (스택 다이얼로그 포커스 충돌 회피) */}
    {showCreateWSModal && <WorkspaceCreateModal show={showCreateWSModal} setShow={setShowCreateWSModal} />}
    {showJoinWSModal && <WorkspaceJoinModal show={showJoinWSModal} setShow={setShowJoinWSModal} />}
    </>
  );
}
